/** The room the player is in (latest server snapshot) and the list of rooms. */
import type { PlayerId, RoomMember, RoomSnapshot, RoomSummary } from '@cube-rush/shared';
import { computed, type ReadonlySignal, type Signal, signal } from '@preact/signals';

const NO_MEMBERS: readonly RoomMember[] = [];

export class RoomStore {
  private readonly roomSignal: Signal<RoomSnapshot | null> = signal(null);
  private readonly roomsSignal: Signal<readonly RoomSummary[]> = signal([]);

  /** null while not in a room (connecting, or between leaving one room and joining another). */
  readonly room: ReadonlySignal<RoomSnapshot | null> = this.roomSignal;
  /** Room list from the last `rooms` answer. */
  readonly rooms: ReadonlySignal<readonly RoomSummary[]> = this.roomsSignal;
  readonly members: ReadonlySignal<readonly RoomMember[]>;
  /** The local player's entry in the room. */
  readonly me: ReadonlySignal<RoomMember | null>;
  readonly isHost: ReadonlySignal<boolean>;

  constructor(myId: ReadonlySignal<PlayerId | null>) {
    this.members = computed(() => this.roomSignal.value?.members ?? NO_MEMBERS);
    this.me = computed(() => {
      const id = myId.value;
      return this.members.value.find((member) => member.id === id) ?? null;
    });
    this.isHost = computed(() => {
      const room = this.roomSignal.value;
      return room !== null && room.hostId !== null && room.hostId === myId.value;
    });
  }

  setRoom(room: RoomSnapshot): void {
    this.roomSignal.value = room;
  }

  setRooms(rooms: readonly RoomSummary[]): void {
    this.roomsSignal.value = rooms;
  }

  clear(): void {
    this.roomSignal.value = null;
  }
}
